import { z } from 'zod';
import type { Role } from './enums';

export const PermissionSchema = z.enum([
  'dashboard:read',
  'school:read',
  'school:create',
  'school:update',
  'teacher:read',
  'student:read',
  'student:write',
  'class:read',
  'class:write',
  'grade:read',
  'grade:write',
  'attendance:read',
  'attendance:write',
  'feedback:read',
  'feedback:write',
  'event:read',
  'event:write',
  'report:read',
  'report:generate',
  'ai:read',
  'ai:generate',
  'audit:read',
  // Usuários da escola (criar com senha provisória, redefinir, inativar)
  'user:manage',
  // OuvidorIA do TaskAll Responsáveis: perguntas dos responsáveis e base de conhecimento da escola
  'ouvidoria:read',
  'ouvidoria:reply',
  'knowledge:manage',
  // Plataforma (somente equipe TaskAll)
  'platform:analytics',
  'platform:health',
]);
export type Permission = z.infer<typeof PermissionSchema>;

const PLATFORM_ONLY: Permission[] = ['school:create', 'platform:analytics', 'platform:health'];

/** Tudo o que a gestão de uma escola pode fazer (sem permissões exclusivas da equipe TaskAll). */
const SCHOOL_ALL: Permission[] = PermissionSchema.options.filter((p) => !PLATFORM_ONLY.includes(p));

/** Atendimento aos responsáveis (OuvidorIA): somente direção e coordenação da escola. */
const OUVIDORIA: Permission[] = ['ouvidoria:read', 'ouvidoria:reply', 'knowledge:manage'];

const READ_ONLY: Permission[] = [
  'dashboard:read',
  'school:read',
  'teacher:read',
  'student:read',
  'class:read',
  'grade:read',
  'attendance:read',
  'feedback:read',
  'event:read',
  'report:read',
  'ai:read',
];

/**
 * Matriz explícita de permissões por papel.
 * Qualquer alteração aqui afeta frontend (visibilidade) e backend (autorização).
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  /**
   * Equipe TaskAll (e-mail verificado @taskall.ai): cadastra escolas, vê a visão analítica e a
   * saúde das aplicações; consulta dados das escolas somente em leitura; não acessa agenda nem relatórios.
   */
  PLATFORM_ADMIN: [
    ...READ_ONLY.filter((p) => p !== 'event:read' && p !== 'report:read'),
    'school:create',
    'school:update',
    'user:manage',
    'platform:analytics',
    'platform:health',
  ],
  NETWORK_ADMIN: SCHOOL_ALL.filter((p) => !OUVIDORIA.includes(p)),
  SCHOOL_DIRECTOR: SCHOOL_ALL,
  COORDINATOR: [
    ...READ_ONLY,
    'student:write',
    'class:write',
    'grade:write',
    'attendance:write',
    'feedback:write',
    'event:write',
    'report:generate',
    'ai:generate',
    'ouvidoria:read',
    'ouvidoria:reply',
  ],
  TEACHER: [
    'dashboard:read',
    'school:read',
    'teacher:read',
    'student:read',
    'class:read',
    'grade:read',
    'grade:write',
    'attendance:read',
    'attendance:write',
    'feedback:read',
    'feedback:write',
    'event:read',
  ],
  VIEWER: READ_ONLY,
};

export function permissionsFor(role: Role): Permission[] {
  return [...ROLE_PERMISSIONS[role]];
}

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Papéis cujo acesso a turmas/alunos é restrito às turmas das quais são tutores. */
export function isClassScopedRole(role: Role): boolean {
  return role === 'TEACHER';
}
